import { FireFilled, RightOutlined } from '@ant-design/icons'
import { Button, Card, Col, Row, Space, Tag, Typography } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { getTeamWorkIdeaCount } from '../../api/aposWorkIdea.api'
import { isAopsChiefState } from '../../store/aops'
import { userState } from '../../store/user'
import '../electricApproval/electricApproval.scss'
import MemberSelect from './components/MemberSelect'
import WorkIdeaFormModal from './components/WorkIdeaFormModal'
import WorkIdeaListModal from './components/WorkIdeaListModal'

const { Text } = Typography

// --- 유틸 함수들은 동일 (getTaskCardTitle, mapPriorityToFrontendColor 등) ---
const getTaskCardTitle = (createType) => {
  switch (createType) {
    case 'STORY':
      return 'Initiative'
    case 'EPIC':
      return 'Objective'
    case 'TASK':
      return 'Task'
    default:
      return ''
  }
}

const mapProgressStateAndPriorityToFrontendColor = (
  progressState,
  priority,
) => {
  const mapInProgressPriorityToFrontendColor = (p) => {
    switch (p) {
      case 'FIRST':
        return '#FF7853'
      case 'SECOND':
        return '#FFCA00'
      case 'THIRD':
        return '#1FC297'
      default:
        return ''
    }
  }
  const mapTodoPriorityToFrontendColor = (p) => {
    switch (p) {
      case 'FIRST':
        return '#FFCCB5'
      case 'SECOND':
        return '#FFF0B5'
      case 'THIRD':
        return '#D9FCED'
      default:
        return ''
    }
  }
  switch (progressState) {
    case 'TODO':
      return mapTodoPriorityToFrontendColor(priority)
    case 'IN_PROGRESS':
      return mapInProgressPriorityToFrontendColor(priority)
    case 'IN_REVIEW':
      return mapInProgressPriorityToFrontendColor(priority)
    case 'DONE':
      return mapTodoPriorityToFrontendColor(priority)
    case 'ON_HOLD':
      return mapTodoPriorityToFrontendColor(priority)
    default:
      return ''
  }
}

const mapPriorityToFrontendColor = (priority) => {
  switch (priority) {
    case 'FIRST':
      return '#FF7853'
    case 'SECOND':
      return '#FFCA00'
    case 'THIRD':
      return '#1FC297'
    default:
      return ''
  }
}

const mapProblemCountToFrontendText = (createType, problemCount) => {
  // console.log('createType', createType)
  // console.log('problemCount', problemCount)
  if (createType === 'STORY') {
    return '이슈 발생 | ' + problemCount
  }
  return '이슈 발생'
}

const MinimalTaskCard = ({ task, onLoadTaskDetail, createType }) => (
  <div onClick={() => onLoadTaskDetail(task)}>
    <Card
      bordered={false}
      style={{
        marginBottom: '10px',
        borderRadius: '8px',
        border: '1px solid #E2EAEF', // 사진처럼 연한 테두리
        cursor: 'pointer',
      }}
      bodyStyle={{ padding: '4px 16px' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* 상단 Epic 텍스트 */}
          <div
            style={{
              fontSize: '12px',
              color: '#A4ADB4',
              fontWeight: '500',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getTaskCardTitle(createType)}
          </div>
          {/* 중앙 제목 */}
          <div
            style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#6D747A',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task.worksInfoDto.title}
          </div>
        </div>
        {/* 우측 화살표 */}
        <RightOutlined
          style={{ color: '#B0B8C1', fontSize: '16px', flexShrink: 0 }}
        />
      </div>
    </Card>
  </div>
)

// --- TaskCard 및 KanbanColumn 컴포넌트 ---
const TaskCard = ({ task, onLoadTaskDetail, createType, color, isStrong }) => (
  <div onClick={() => onLoadTaskDetail(task)}>
    <Card
      className="task-card"
      bordered={false}
      style={{
        marginBottom: '16px',
        borderRadius: '12px',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #D8E1E6',
        cursor: 'pointer',
      }}
      bodyStyle={{ padding: '10px 16px 10px 16px' }}
    >
      {/* 왼쪽 색상 띠 (기존 스타일 유지) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '16px',
          height: '44px',
          borderRadius: '0 0 12px 0',
          background: color,
          zIndex: 1,
        }}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: '16px',
          minWidth: 0,
        }}
      >
        {/* 상단 영역: 종류와 우측 아이콘 세트(이슈 칩 + 화살표) */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            type="secondary"
            style={{
              fontSize: '16px',
              fontWeight: '500',
              color: isStrong ? '#191A1C' : '#6D747A',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getTaskCardTitle(createType)}
          </Text>

          {/* 우측 아이콘 세트: 이슈 칩이 화살표 왼쪽에 위치 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
            }}
          >
            {(task.worksInfoDto.hasProblem ||
              task.storyChildProblemCount > 0) &&
              task.createType !== 'EPIC' && (
                <Tag
                  icon={
                    <FireFilled
                      style={{
                        color:
                          task.worksInfoDto.problemState ===
                          'RESOLVED_OVERDUE_REVIEW_TIME'
                            ? '#6D747A'
                            : '#F25A67',
                      }}
                    />
                  }
                  style={{
                    borderRadius: '6px',
                    fontSize: '10px',
                    margin: 0,
                    background: '#fff',
                    color:
                      task.worksInfoDto.problemState ===
                      'RESOLVED_OVERDUE_REVIEW_TIME'
                        ? '#6D747A'
                        : '#F25A67',
                    border:
                      task.worksInfoDto.problemState ===
                      'RESOLVED_OVERDUE_REVIEW_TIME'
                        ? '1px solid #6D747A'
                        : '1px solid #F25A67',
                    fontWeight: '700',
                    padding: '6px 6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0px',
                  }}
                >
                  {/* <Text
                    style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#F25A67',
                    }}
                  >이슈 발생</Text> */}
                  {task.storyChildProblemCount > 0 && (
                    <>
                      <Text
                        style={{
                          fontSize: '12px',
                          fontWeight: '800',
                          color: '#F25A67',
                        }}
                      >
                        {task.storyChildProblemCount}
                      </Text>
                    </>
                  )}
                </Tag>
              )}
            <RightOutlined style={{ color: '#A4ADB4', fontSize: '18px' }} />
          </div>
        </div>

        {/* 중앙 영역: Task 이름 */}
        <div
          style={{
            fontWeight: '700',
            fontSize: '16px',
            marginTop: '12px',
            color: isStrong ? '#191A1C' : '#6D747A',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {task.worksInfoDto.title}
        </div>
      </div>
    </Card>
  </div>
)

// 헬퍼 함수: 생각보관함 버튼 표시 여부
const shouldShowThinkButton = (
  isThinkButton,
  activeLevel,
  thisIsMe,
  isMyTeam,
) => {
  if (!isThinkButton) return false
  if (activeLevel === 'STUDIO') return false
  if (activeLevel === 'MEMBER' && !thisIsMe) return false
  if (activeLevel === 'TEAM' && !isMyTeam) return false
  return true
}

// 헬퍼 함수: 빈 To Do List 표시 여부
const shouldShowEmptyTodoList = (section, activeLevel, thisIsMe) => {
  return (
    section.tasks.length === 0 &&
    section.title === 'To Do List' &&
    activeLevel === 'MEMBER' &&
    thisIsMe
  )
}

// 헬퍼 함수: 섹션 정렬 스타일
const getSectionAlignment = (section) => {
  const isEmptyTodoList =
    section.tasks.length === 0 && section.title === 'To Do List'
  return {
    justifyContent: isEmptyTodoList ? 'center' : 'flex-start',
    alignItems: isEmptyTodoList ? 'center' : 'stretch',
  }
}

// 헬퍼 함수: 팀 카운트 표시
const renderTeamCount = (activeLevel, teamWorkIdeaCount) => {
  if (activeLevel !== 'TEAM') return null
  return (
    <span
      style={{
        color: '#117FFA',
        fontSize: '14px',
        fontWeight: '800',
        letterSpacing: '-0.35px',
      }}
    >
      {teamWorkIdeaCount.totalCount}
    </span>
  )
}

// 헬퍼 함수: 읽지 않은 개수 배지
const renderUnreadBadge = (activeLevel, teamWorkIdeaCount) => {
  if (activeLevel !== 'TEAM' || teamWorkIdeaCount.unreadCount <= 0) {
    return null
  }
  return (
    <div
      style={{
        position: 'absolute',
        top: '-4px',
        right: '-4px',
        width: '12px',
        height: '12px',
        backgroundColor: '#117FFA',
        border: '2px solid #FFF',
        borderRadius: '50%',
      }}
    />
  )
}

const KanbanColumn = ({
  sections,
  onLoadTaskDetail,
  createType,
  isMinimal,
  isStrong,
  isThinkButton = false,
  onThinkButtonClick,
  onCreateIdeaClick,
  activeLevel,
  teamWorkIdeaCount = 0,
  thisIsMe = false,
  isMyTeam = false,
}) => {
  const showThinkButton = shouldShowThinkButton(
    isThinkButton,
    activeLevel,
    thisIsMe,
    isMyTeam,
  )

  return (
    <Col
      style={{
        flex: '0 0 25%',
        maxWidth: '25%',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        // marginBottom: '12px',
      }}
    >
      {sections.map((section, index) => (
        <div
          key={index}
          className="kanban-column"
          style={{
            background: '#FFF',
            padding: '16px',
            borderRadius: '16px',
            // 구역이 2개일 때는 높이를 적절히 나누고, 1개일 때는 기존 높이(400px) 유지
            height: sections.length > 1 ? '190px' : '400px',
            display: 'flex',
            flexDirection: 'column',
            // border: '1px solid #E2EAEF',
            boxShadow: '0 2px 8px #e2eaef',
          }}
        >
          {/* 헤더 영역: 타이틀 및 숫자 카운트 */}
          <div
            style={{
              display: 'flex',
              marginBottom: '20px',
              padding: '0 4px',
              flexShrink: 0,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text
              strong
              style={{
                fontSize: '20px',
                fontWeight: '800',
                color: isStrong ? '#1A1A1A' : '#6D747A',
                letterSpacing: '-0.35px',
              }}
            >
              {section.title}
              <span
                style={{
                  color: isStrong ? '#117FFA' : '#A0CCFD',
                  marginLeft: '12px',
                }}
              >
                {section.tasks.length > 0
                  ? section.tasks.length.toString().padStart(2, '0')
                  : ''}
              </span>
            </Text>
            {showThinkButton && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Button
                  style={{
                    border: '1px solid #B7C0C7',
                    boxShadow: '2px 2px 8px #e2eaef',
                    borderRadius: '6px',
                    padding: '6px 8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    letterSpacing: '-0.35px',
                    color: '#383E44',
                  }}
                  onClick={onThinkButtonClick}
                >
                  생각보관함
                  {renderTeamCount(activeLevel, teamWorkIdeaCount)}
                </Button>
                {renderUnreadBadge(activeLevel, teamWorkIdeaCount)}
              </div>
            )}
          </div>

          {/* 스크롤 가능한 테스크 리스트 영역 */}
          <div
            className="task-list-scroll-area"
            style={{
              flex: 1,
              overflowY: 'auto',
              paddingRight: '4px',
              display: 'flex',
              flexDirection: 'column',
              ...getSectionAlignment(section),
            }}
          >
            {shouldShowEmptyTodoList(section, activeLevel, thisIsMe) ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 20px',
                  textAlign: 'center',
                }}
              >
                <Text
                  style={{
                    fontSize: '16px',
                    color: '#585F65',
                    letterSpacing: '-0.35px',
                    fontWeight: '500',
                    lineHeight: '1.5',
                    marginBottom: '24px',
                  }}
                >
                  현재 TO DO가 없어요
                  <br />
                  자유롭게 업무를 생각해봐요
                </Text>
                <Button
                  type="primary"
                  onClick={onCreateIdeaClick}
                  style={{
                    borderRadius: '8px',
                    height: '40px',
                    fontSize: '14px',
                    fontWeight: '600',
                    padding: '0 20px',
                  }}
                >
                  생각노트 적어보기 <RightOutlined />
                </Button>
              </div>
            ) : (
              section.tasks.map((task) =>
                // isMinimal 플래그에 따라 카드 컴포넌트 선택
                isMinimal ? (
                  <MinimalTaskCard
                    key={task.worksInfoDto.id}
                    task={task}
                    onLoadTaskDetail={onLoadTaskDetail}
                    createType={createType}
                  />
                ) : (
                  <TaskCard
                    key={task.worksInfoDto.id}
                    task={task}
                    onLoadTaskDetail={onLoadTaskDetail}
                    createType={createType}
                    color={mapProgressStateAndPriorityToFrontendColor(
                      task.worksInfoDto.progressState,
                      task.worksInfoDto.priority,
                    )}
                    isStrong={isStrong}
                  />
                ),
              )
            )}
          </div>
        </div>
      ))}
    </Col>
  )
}

const columnConfigs = [
  {
    group: ['To Do List'],
    isMinimal: false,
    isStrong: false,
    isThinkButton: true,
  },
  {
    group: ['In Progress'],
    isMinimal: false,
    isStrong: true,
    isThinkButton: false,
  },
  {
    group: ['In Review'],
    isMinimal: false,
    isStrong: true,
    isThinkButton: false,
  },
  {
    group: ['Done', 'On hold'],
    isMinimal: true,
    isStrong: false,
    isThinkButton: false,
  }, // 이 컬럼은 MinimalCard 사용
]
// --- Main KanbanBoard Component ---
const KanbanBoard = ({
  tasks,
  activeLevel,
  setActiveLevel,
  selectedMember,
  setSelectedMember,
  representativeEpic,
  selectedStudio, // ID 값
  selectedTeam, // teamName
  selectedStudioKo, // 실제 이름
  selectedTeamKo, // 실제 이름
  createType,
  isModalOpen,
  setIsModalOpen,
  selectedTask,
  setSelectedTask,
  onReloadList,
  isUserInGroup,
  teamUsers,
  isChief,
  finalSelectedMemberId,
  isMyTeam = false,
  onOpenPopup,
}) => {
  const showCreateButton = activeLevel !== 'MEMBER'
  const [isAopsCheif] = useRecoilState(isAopsChiefState)
  const [user] = useRecoilState(userState)

  // 생각보관함 모달 상태
  const [isWorkIdeaListModalOpen, setIsWorkIdeaListModalOpen] = useState(false)
  const [isWorkIdeaFormModalOpen, setIsWorkIdeaFormModalOpen] = useState(false)
  const [selectedWorkIdea, setSelectedWorkIdea] = useState(null)
  const [teamWorkIdeaCount, setTeamWorkIdeaCount] = useState(0)
  const [workIdeaListModalMode, setWorkIdeaListModalMode] = useState('team')
  const [previousModalMode, setPreviousModalMode] = useState('team')

  const thisIsMe = finalSelectedMemberId === user.id

  const handleLoadTaskDetail = async (task) => {
    // onOpenPopup이 있으면 사용 (SchedulePage에서 전달된 경우)
    if (onOpenPopup) {
      // EPIC 보드에서는 보드 레벨(createType)을 우선 → ObjectiveDetailPopup
      // 그 외에는 task.createType 우선, 없으면 createType
      const taskType =
        createType === 'EPIC'
          ? 'EPIC'
          : (task.createType || createType) === 'STORY'
            ? 'STORY'
            : 'TASK'
      onOpenPopup(taskType, task)
      return
    }
    // 기존 방식 (ScrumBoard 등에서 사용하는 경우)
    setSelectedTask(task)
    setIsModalOpen(true)
  }

  const handleThinkButtonClick = () => {
    setWorkIdeaListModalMode(activeLevel === 'MEMBER' ? 'personal' : 'team')
    setIsWorkIdeaListModalOpen(true)
  }

  const handleCreateIdeaClick = () => {
    // 생성은 항상 개인 모드로 열리게 설정
    setPreviousModalMode('personal')
    setSelectedWorkIdea(null)
    setIsWorkIdeaFormModalOpen(true)
  }

  const handleEditIdeaClick = (idea) => {
    // 수정 전 모드를 저장 (수정 완료 후 같은 모드로 열기 위해)
    setPreviousModalMode(workIdeaListModalMode)
    setSelectedWorkIdea(idea)
    setIsWorkIdeaFormModalOpen(true)
    setIsWorkIdeaListModalOpen(false)
  }

  const fetchTeamWorkIdeaCount = useCallback(async () => {
    if (!selectedTeam || !user?.id) {
      return
    }
    try {
      const res = await getTeamWorkIdeaCount(selectedTeam, user.id)
      // console.log('🐎fetchTeamWorkIdeaCount res = ', res)
      setTeamWorkIdeaCount({
        totalCount: res?.data?.totalCount || 0,
        unreadCount: res?.data?.unreadCount || 0,
      })
    } catch (error) {
      console.error('팀 생각보관함 개수 조회 실패:', error)
      setTeamWorkIdeaCount({
        totalCount: 0,
        unreadCount: 0,
      })
    }
  }, [selectedTeam, user?.id])

  const handleWorkIdeaFormSuccess = () => {
    setIsWorkIdeaFormModalOpen(false)
    setSelectedWorkIdea(null)
    // 수정 전 모드로 리스트 모달 열기 (팀 모드에서 수정했으면 팀 모드로 유지)
    setWorkIdeaListModalMode(previousModalMode)
    setIsWorkIdeaListModalOpen(true)
    // 팀 생각보관함 개수도 새로고침
    if (activeLevel === 'TEAM' && selectedTeam) {
      fetchTeamWorkIdeaCount()
    }
  }

  useEffect(() => {
    if (activeLevel === 'TEAM' && selectedTeam) {
      fetchTeamWorkIdeaCount()
    } else {
      setTeamWorkIdeaCount({
        totalCount: 0,
        unreadCount: 0,
      })
    }
  }, [activeLevel, selectedTeam, fetchTeamWorkIdeaCount])

  return (
    <div className="kanban-section">
      {/* 상단 버튼 영역 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        {showCreateButton && isUserInGroup && (
          <></>
          // <button
          //   // disabled={disabledRegister}
          //   className={'register-button'}
          //   style={{ position: 'absolute', right: '0px', bottom: '0px' }}
          //   onClick={() => {
          //     setSelectedTask(null)
          //     setIsModalOpen(true)
          //   }}
          // >
          //   {createType === 'STORY'
          //     ? 'Initiative 업무 생성'
          //     : 'Objective 업무 생성'}
          //   <PlusOutlined style={{ marginLeft: '4px', fontWeight: `700` }} />
          // </button>
          // <Button
          //   type="primary"
          //   icon={<PlusOutlined />}
          //   iconPosition="end"
          //   style={{
          //     background: '#FFF',
          //     color: '#117FFA',
          //     border: '1px solid #117FFA',
          //     borderRadius: '8px',
          //     boxShadow: '2px 2px 8px 0 rgba(204, 211, 215, 0.30)',
          //     fontWeight: '700',
          //     height: '45px',
          //     fontSize: '16px',
          //   }}
          //   onClick={() => {
          //     setSelectedTask(null)
          //     setIsModalOpen(true)
          //   }}
          // >
          //   {createType === 'STORY' ? 'STORY 업무 생성' : 'EPIC 업무 생성'}
          // </Button>
        )}
      </div>

      <div
        style={{
          background: '#fff',
          padding: '20px 32px',
          borderRadius: '12px',
          marginBottom: '8px',
          boxShadow: '0 2px 8px #e2eaef',
        }}
      >
        <Row align="top" gutter={68}>
          <Col>
            <Text
              style={{
                fontSize: '16px',
                display: 'block',
                marginBottom: '12px',
                fontWeight: '500',
                color: '#585f65',
              }}
            >
              그룹 / 개인 선택
            </Text>
            <Space size={8}>
              <Button
                style={getButtonStyle(activeLevel === 'STUDIO')}
                onClick={() => {
                  setActiveLevel('STUDIO')
                  // setSelectedMember(null)
                }}
              >
                {selectedStudioKo || 'Studio'}{' '}
                {/* Props로 받은 한글 이름 직접 사용 */}
              </Button>
              <Button
                style={getButtonStyle(activeLevel === 'TEAM')}
                onClick={() => {
                  setActiveLevel('TEAM')
                  // setSelectedMember(null)
                }}
              >
                {selectedTeamKo || 'Team'}{' '}
                {/* Props로 받은 한글 이름 직접 사용 */}
              </Button>
              <MemberSelect
                placeholder="개인"
                value={selectedMember?.id}
                teamUsers={teamUsers}
                isActive={activeLevel === 'MEMBER'}
                setSelectedMember={setSelectedMember}
                setActiveLevel={setActiveLevel}
                currentUser={user}
                isMyTeam={isMyTeam}
              />
            </Space>
          </Col>

          <Col>
            <Text
              style={{
                fontSize: '16px',
                display: 'block',
                marginBottom: '12px',
                fontWeight: '500',
                color: '#585f65',
              }}
            >
              금주 Objective
            </Text>
            <div
              style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                // justifyContent: 'flex-end',
              }}
            >
              {/* representativeEpic 배열의 0, 1번째 아이템 렌더링 */}
              {[0, 1].map((index) => {
                const epic = representativeEpic[index]
                if (!epic) return null // 데이터가 없을 경우 방어 코드

                return (
                  <div
                    key={epic.id || index}
                    style={{
                      position: 'relative',
                      background: '#F6F7FA',
                      border: '1px solid #E2EAEF',
                      // 왼쪽 패딩(24px)은 띠(6px) + 텍스트 여백을 고려한 값입니다.
                      padding: '12px 16px 12px 24px',
                      height: '48px',
                      borderRadius: '8px',
                      // 너비를 200px 정도로 고정하고 줄어들지 않게 설정합니다.
                      width: '300px',
                      flexShrink: 0,
                      fontSize: '15px',
                      fontWeight: '600',
                      overflow: 'hidden',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                      display: 'flex',
                      alignItems: 'center', // 텍스트를 세로 중앙 정렬
                      justifyContent: 'flex-start', // 텍스트를 왼쪽 정렬
                    }}
                  >
                    {/* 왼쪽 우선순위 띠 (Priority 기준) */}
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '6px', // 띠 너비
                        background: mapPriorityToFrontendColor(epic.priority), // 우선순위 색상 적용
                      }}
                    />

                    {/* 텍스트 영역: 왼쪽 정렬 및 말줄임표 처리 */}
                    <Text
                      ellipsis={{ tooltip: epic.title }} // 긴 제목은 툴팁으로 보여줌
                      style={{
                        color: '#191A1C',
                        width: '100%',
                        textAlign: 'left',
                        lineHeight: '1.4',
                      }}
                    >
                      {epic.title}
                    </Text>
                  </div>
                )
              })}
            </div>
          </Col>
        </Row>
      </div>

      <Row gutter={10} style={{ display: 'flex' }}>
        {columnConfigs.map((config, idx) => (
          <KanbanColumn
            key={idx}
            isMinimal={config.isMinimal} // 컬럼 단위로 스타일 결정
            sections={config.group.map((status) => ({
              title: status,
              tasks: tasks.filter((t) => t.status === status),
            }))}
            onLoadTaskDetail={handleLoadTaskDetail}
            createType={createType}
            isStrong={config.isStrong}
            isThinkButton={config.isThinkButton}
            onThinkButtonClick={handleThinkButtonClick}
            onCreateIdeaClick={handleCreateIdeaClick}
            activeLevel={activeLevel}
            teamWorkIdeaCount={teamWorkIdeaCount}
            thisIsMe={thisIsMe}
            isMyTeam={isMyTeam}
          />
        ))}
      </Row>

      {/* 생각보관함 리스트 모달 */}
      <WorkIdeaListModal
        open={isWorkIdeaListModalOpen}
        onClose={() => {
          setIsWorkIdeaListModalOpen(false)
          setSelectedWorkIdea(null)
          // 모달 닫을 때 refetch
          if (activeLevel === 'TEAM' && selectedTeam) {
            fetchTeamWorkIdeaCount()
          }
        }}
        salusUserId={user?.id}
        onEditClick={handleEditIdeaClick}
        onCreateClick={() => {
          setIsWorkIdeaListModalOpen(false)
          handleCreateIdeaClick()
        }}
        mode={workIdeaListModalMode}
      />

      {/* 생각노트 생성/수정 모달 */}
      <WorkIdeaFormModal
        open={isWorkIdeaFormModalOpen}
        onClose={() => {
          setIsWorkIdeaFormModalOpen(false)
          setSelectedWorkIdea(null)
          // 모달 닫을 때 refetch
          if (activeLevel === 'TEAM' && selectedTeam) {
            fetchTeamWorkIdeaCount()
          }
        }}
        salusUserId={user?.id}
        initialData={selectedWorkIdea}
        onSuccess={handleWorkIdeaFormSuccess}
      />
    </div>
  )
}

const getButtonStyle = (isActive) => ({
  borderRadius: '12px',
  background: isActive ? '#000000' : '#fff',
  color: isActive ? '#fff' : '#383E44',
  border: isActive ? '1px solid #000000' : '1px solid #E2EAEF',
  fontSize: '16px',
  height: '48px',
  padding: '0 20px',
  fontWeight: isActive ? '700' : '600',
})

export default KanbanBoard
